<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    v-bind:disabled="isDisabledSelectingNode"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot v-bind:selectedNode="selectedNode"></slot>
      </div>
    </template>

    <v-card v-cloak data-cy="dialog---">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog---">
        {{ $t('routes.users.selectFolder') }}

        <v-btn icon @click="cancel()" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text scrollable class="scrollable" data-cy="dialog------">
        <span v-if="browseNode" class="text-subtitle-1 mb-4 d-block">
          <p>{{$t('routes.users.currentFolder')}}:</p>
          <p><strong>{{browseNode.name}}</strong></p>
        </span>

        <v-list
          v-if="(nodeList && nodeList.length > 0) || (podsList && podsList.length > 0)"
          open-strategy="list"
          select-strategy="classic"
          selectable
          @update:selected="val => (listSelectedItems = val)"
        >
          <v-list-item
            v-for="node in nodeList"
            @dblclick="displayNode(node.id)"
            v-bind:key="node.id"
            v-bind:value="node.id"
            data-cy="dialog-copySelected-listItem"
          >
            <template v-slot:prepend>
              <v-tooltip location="bottom" z-index="2000" open-delay="200" :aria-label="getNodeType(node)">
                <template v-slot:activator="{ props }">
                  <v-icon v-bind="props">
                    {{ getNodeIconName(node) }}
                  </v-icon>
                </template>
                <span>{{ getNodeType(node) }}</span>
              </v-tooltip>
            </template>
            <v-tooltip location="bottom" z-index="2000" open-delay="200" :aria-label="node.name">
              <template v-slot:activator="{ props }">
                <v-list-item-title v-bind="props" class="pl-1"
                                   data-cy="dialog-copySelected-itemContent">
                  {{ node.name }}
                </v-list-item-title>
              </template>
              <span>{{ node.name }}</span>
            </v-tooltip>
          </v-list-item>
          <v-list-item v-for="pod in podsList" v-bind:key="pod.id" v-bind:value="pod.id">
            <template v-slot:prepend>
              <v-icon>mdi-file</v-icon>
            </template>
            <v-tooltip location="bottom" z-index="2000" open-delay="200" :aria-label="pod.name">
              <template v-slot:activator="{ props }">
                <v-list-item-title v-bind="props">
                  {{ pod.name }}
                </v-list-item-title>
              </template>
              <span>{{ pod.name }}</span>
            </v-tooltip>
          </v-list-item>
        </v-list>
        <div v-if="(!nodeList || nodeList.length === 0) && (!podsList || podsList.length === 0)" class="mt-4">
          {{ $t('routes.node.noFolders') }}
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="!!browseNode"
          v-on:click="displayNode(browseNode.parentid)"
          variant="text" data-cy="dialog-copySelected-back-btn"
        >
          <v-icon>mdi-arrow-left</v-icon>
          {{ $t('common.back') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="submit()" variant="flat" v-bind:disabled="okDisabled" data-cy="dialog---">
          {{ $t('common.select') }}
        </v-btn>
        <v-btn variant="text" @click="cancel()" data-cy="dialog---">
          {{ $t('common.cancel') }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  mixins: [ DialogActionKeys ],
  name: 'SelectPodsDialog',
  emits: ['selectedPods', 'selectedNodes'],
  props: {
    isDisabledSelectingNode: {
      type: Boolean,
    },
    browseNodeId: {
      type: String,
      required: false,
    },
  },
  data: function () {
    return {
      dialog: false,
      errorMessage: '',
      browseNode: null,
      selectedNode: null,
      listSelectedNodes: [],
      listSelectedPods: [],
      listSelectedItems: [],
    }
  },
  mounted() {
    if (this.browseNodeId) {
      this.displayNode(this.browseNodeId, true);
    }
  },
  watch: {
    browseNodeId(newValue) {
      if (newValue) {
        this.displayNode(newValue, true);
      }
    },
    dialog(newValue) {
      if (!newValue) {
        this.displayNode(this.browseNodeId, true);
      }
    },
    listSelectedItems(selectedItemIds) {
      this.listSelectedPods = [];
      this.listSelectedNodes = [];

      if (selectedItemIds.length > 0) {
        for (const val of selectedItemIds) {
          if (Array.isArray(this?.browseNode?.pods) && this.browseNode.pods.some((p) => p.id === val)) {
            this.listSelectedPods.push(val);
          } else if (Array.isArray(this?.browseNode?.children) && this.browseNode.children.some((n) => n.id === val)) {
            this.listSelectedNodes.push(val);
          }
        }
      }
    },
  },
  methods: {
    cancel: function () {
      this.dialog = false;
      this.errorMessage = '';
      this.listSelectedNodes = [];
      this.listSelectedPods = [];

      this.browseNode = this.selectedNode;
    },
    displayNode: async function (nodeID, updateSelectedNode = false) {
      this.listSelectedNodes = [];
      this.listSelectedPods = [];

      if (nodeID) {
        const nodeStore = useNodeStore();
        this.browseNode = await nodeStore.getNodeInfo(nodeID);
        this.listSelectedNodes = [];
        this.listSelectedPods = [];

        const podStore = usePodStore();
        podStore.fetchPodsByNodeId(nodeID)
          .catch(err => {
            console.error('Error fetching pods for node:', err);
          });
      } else {
        this.browseNode = null;
      }

      if (updateSelectedNode) {
        this.selectedNode = this.browseNode;
      }
    },
    submit: function () {
      if (this.browseNode) {
        if (this.listSelectedPods.length > 0) {
          this.$emit('selectedPods', this.listSelectedPods);
        }

        if (this.listSelectedNodes.length > 0) {
          this.$emit('selectedNodes', this.listSelectedNodes);
        }

        this.selectedNode = this.browseNode;
        this.listSelectedPods = [];
        this.listSelectedNodes = [];
      }

      this.dialog = false;
    },
  },
  computed: {
    nodeList: function () {
      if (this.browseNode) return this.browseNode.children || [];
      return this.accessPoints || [];
    },
    podsList: function () {
      if (this.browseNode && this.browseNode.id) {
        const podStore = usePodStore();
        return podStore.getPodsByNodeId(this.browseNode.id);
      }

      return [];
    },
    accessPoints: function () {
      const nodeStore = useNodeStore();
      return nodeStore.getAccessPoints;
    },
    getNodeType: function () {
      const commonStore = useCommonStore();
      const TYPES = commonStore.nodeTypes;

      let self = this;
      return (node) => {
        switch (node.type) {
          case TYPES.FOLDER:
            return self.$t('routes.node.typeFolder');
          case TYPES.PROJECT:
            return self.$t('routes.node.typeProject');
          case TYPES.CLIENT:
            return self.$t('routes.node.typeClient');
          case TYPES.TENANT:
            return self.$t('routes.node.typeTenant');
          default:
            return self.$t('routes.node.typeUnknown');
        }
      }
    },
    getNodeIconName: function () {
      return (node) => {
        let iconName;
        switch (node.type) {
          case 2:
            iconName = "mdi-briefcase-clock-outline";
            break;
          case 3:
            iconName = "tw tw-office-building";
            break;
          case 4:
            iconName = "mdi-home-assistant";
            break;
          default:
            iconName = "mdi-folder-outline";
            break;
        }
        return iconName;
      };
    },
    okDisabled: function () {
      return this.listSelectedPods === null || this.listSelectedPods === undefined;
    },
  }
}
</script>
